import cookie from 'cookie';
import type { Cookies as SvelteCookies } from '@sveltejs/kit';

export type CookieOpts = {
    maxAge?: number;
    path: string;
    secure: boolean;
    httpOnly: boolean;
    sameSite: 'lax' | 'strict' | 'none';
};

interface CookieInterface {
    get(name: string): string | undefined;
    set(name: string, data: string, opts?: CookieOpts): void;
    delete(name: string): void;
}

function getCookiesFromDocument(document: Document): Record<string, string> {
    try {
        return cookie.parse(document.cookie);
    } catch (e) {
        console.warn('Failed to parse cookies', e);
        return {};
    }
}

export function fromDocument(document: Document): CookieInterface {
    return {
        get: (name: string) => getCookiesFromDocument(document)[name],
        set: (name: string, value: string, opts?: CookieOpts) =>
            (document.cookie = cookie.serialize(name, value, opts)),
        delete: (name: string) => {
            document.cookie = `${name}=;Max-Age=0`;
        }
    };
}

export function fromSvelteCookies(cookies: SvelteCookies): CookieInterface {
    return cookies as unknown as CookieInterface;
}

// Thin abstraction on top of 'cookie' to handle client and server side (Document and RequestEvent)
// And to handle some common cases like deleting cookies by setting value to null or undefined.
export class Cookies {
    private cookieInterface: CookieInterface;

    constructor(cookieInterface: CookieInterface) {
        this.cookieInterface = cookieInterface;
    }
    // Sets a cookie. A (null | undefined) value will delete the cookie
    // Will be a no-op if the value is not changing, unless force == true.
    // Forcing can be neccesary if the value is unchanged but the opts need updating.
    set(
        name: string,
        value: string | null | undefined,
        opts: CookieOpts = { path: '/', secure: false, httpOnly: false, sameSite: 'lax' },
        force = false
    ) {
        if (!force && this.get(name) == value) return;
        if (value == null || value == undefined) {
            //console.info(`Cookie delete '${name}'`)
            this.cookieInterface.delete(name);
        } else {
            //console.info(`Cookie set '${name}' -> '${value}'`)
            this.cookieInterface.set(name, value, opts);
        }
    }
    get(name: string) {
        return this.cookieInterface.get(name);
    }
}
